import {
	Button,
	Code,
	Fieldset,
	Grid,
	Group,
	LoadingOverlay,
	Modal,
	NumberInput,
	Select,
	Space,
	Text,
} from '@mantine/core';
import {
	IconArrowRoundaboutLeft,
	IconCancel,
	IconCoinPound,
	IconFileDollar,
	IconFileStack,
	IconMoneybag,
} from '@tabler/icons-react';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import {
	defaultIconStyle,
	inputIconStyle,
} from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { useVarianceForm } from '../hooks/useVarianceForm';
import type { BankingDto, VarianceDto } from '../types/BankingDto';

interface VarianceModalProps {
	close: () => void;
	isOpened: boolean;
	updateTableRow: Dispatch<SetStateAction<BankingDto>>;
	initialBank: VarianceDto;
}

export function VarianceModal({
	isOpened,
	close,
	updateTableRow,
	initialBank,
}: VarianceModalProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useVarianceForm(initialBank);

	const varianceMutation = mutate<VarianceDto, BankingDto>(
		`${organisation}/banking/variance`,
		inputs,
	);

	const resetAndClose = () => {
		form.reset();
		close();
		successModal.close();
	};

	const updateAndClose = () => {
		if (varianceMutation.data?.value) {
			updateTableRow(varianceMutation.data?.value);
			form.setInitialValues(inputs);
		}

		resetAndClose();
	};

	const inputProps = {
		decimalScale: 2,
		thousandSeparator: ',',
		allowNegative: true,
		fixedDecimalScale: true,
		max: 1000000,
		min: -1000000,
		prefix: '£',
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Add Variance'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
				pt={0}
				mt={0}
			>
				<Code style={{ backgroundColor: 'transparent' }}>
					Any changes to the record will require the data to be reprocessed
				</Code>
				<Space h={10} />
				<LoadingOverlay visible={varianceMutation.isLoading} />
				<Modal
					opened={successModal.isOpen}
					onClose={successModal.close}
					title={<Title label={'Variance added!'} />}
					centered
				>
					<Text>
						A variance has been successfully added. Click Ok to continue...
					</Text>
					<Group justify={'flex-end'}>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCancel {...defaultIconStyle} />}
							onClick={() => updateAndClose()}
						>
							Ok
						</Button>
					</Group>
				</Modal>

				<Space h={10} />
				<form
					onSubmit={form.onSubmit(async () => {
						await varianceMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<Fieldset>
						<Grid>
							<Grid.Col span={6}>
								<NumberInput
									{...inputProps}
									label={'Cash'}
									placeholder="Insert cash..."
									leftSection={<IconMoneybag {...inputIconStyle} />}
									key={form.key('cash')}
									{...form.getInputProps('cash')}
									max={1000000}
									min={-1000000}
								/>
							</Grid.Col>
							<Grid.Col span={6}>
								<NumberInput
									{...inputProps}
									label={'Cash Invoices'}
									placeholder="Insert cash invoices..."
									leftSection={<IconFileDollar {...inputIconStyle} />}
									key={form.key('cashInvoices')}
									{...form.getInputProps('cashInvoices')}
								/>
							</Grid.Col>
							<Grid.Col span={6}>
								<NumberInput
									{...inputProps}
									label={'Float Held'}
									placeholder="Insert cash..."
									leftSection={<IconFileStack {...inputIconStyle} />}
									key={form.key('floatHead')}
									{...form.getInputProps('floatHead')}
								/>
							</Grid.Col>
							<Grid.Col span={6}>
								<NumberInput
									{...inputProps}
									label={'Change Held'}
									placeholder="Insert cash..."
									leftSection={<IconCoinPound {...inputIconStyle} />}
									key={form.key('changeHeld')}
									{...form.getInputProps('changeHeld')}
								/>
							</Grid.Col>
							<Grid.Col>
								<Select
									leftSection={<IconArrowRoundaboutLeft {...inputIconStyle} />}
									label={'Variance Type'}
									placeholder={'Select type of variance...'}
									data={[
										'System error',
										'Staff error',
										'EOD not done',
										'Round error',
										'Other',
									]}
									key={form.key('varReason')}
									{...form.getInputProps('varReason')}
								/>
							</Grid.Col>
						</Grid>
					</Fieldset>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
